'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useCallback, useEffect, type ReactNode } from 'react';
import Link from 'next/link';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
export interface FeedCarouselExploreMore {
  /** Link destination for the explore-more card */
  href: string;
  /** Primary label (e.g. "Explore more") */
  label: string;
  /** Secondary description text */
  description?: string;
}
export interface FeedCarouselProps {
  /** Items to render. Can be ReactNode[] (sync) or a render function that receives loading state. */
  children: ReactNode;
  /** Show navigation arrows for scrolling. Defaults to true. */
  showArrows?: boolean;
  /** Gap between items in pixels. Defaults to 16 (gap-4). */
  gap?: number;
  /** Horizontal padding class for the scroll container. Defaults to "px-5 sm:px-8 lg:px-12". */
  paddingClass?: string;
  /** Optional explore-more card shown at the end of the carousel. */
  exploreMore?: FeedCarouselExploreMore;
  /** Additional className for the outer wrapper */
  className?: string;
  /** Scroll amount per arrow click in pixels. Defaults to 360. */
  scrollAmount?: number;
  /**
   * Visual style for the navigation arrows.
   * - `solid` (default): high-contrast dark buttons for maximum visibility.
   * - `light`: translucent white buttons (previous default look).
   */
  arrowVariant?: 'solid' | 'light';
  /** Override classes for the arrow buttons (merged after the variant styles). */
  arrowClassName?: string;
  /** Override classes for the chevron icons inside the arrows. */
  arrowIconClassName?: string;
}
const ARROW_VARIANTS: Record<NonNullable<FeedCarouselProps['arrowVariant']>, {
  button: string;
  icon: string;
}> = {
  solid: {
    button: 'h-11 w-11 bg-gray-100 text-gray-600 shadow-sm ring-1 ring-gray-200 hover:bg-gray-200 hover:text-gray-800 hover:shadow-md',
    icon: 'h-5 w-5'
  },
  light: {
    button: 'h-9 w-9 bg-white/90 text-gray-700 shadow-md backdrop-blur-sm hover:bg-white',
    icon: 'h-5 w-5'
  }
};
export function FeedCarousel(t0) {
  const $ = _c(40);
  const {
    children,
    showArrows: t1,
    gap: t2,
    paddingClass: t3,
    exploreMore,
    className,
    scrollAmount: t4,
    arrowVariant: t5,
    arrowClassName,
    arrowIconClassName
  } = t0;
  const showArrows = t1 === undefined ? true : t1;
  const gap = t2 === undefined ? 16 : t2;
  const paddingClass = t3 === undefined ? "px-5 sm:px-8 lg:px-12" : t3;
  const scrollAmount = t4 === undefined ? 360 : t4;
  const arrowVariant = t5 === undefined ? "solid" : t5;
  const variant = ARROW_VARIANTS[arrowVariant];
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const updateScrollState = t6;
  let t7;
  let t8;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const el_0 = scrollRef.current;
      if (!el_0) {
        return;
      }
      updateScrollState();
      el_0.addEventListener("scroll", updateScrollState, {
        passive: true
      });
      const resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el_0);
      return () => {
        el_0.removeEventListener("scroll", updateScrollState);
        resizeObserver.disconnect();
      };
    };
    t8 = [updateScrollState];
    $[1] = t7;
    $[2] = t8;
  } else {
    t7 = $[1];
    t8 = $[2];
  }
  useEffect(t7, t8);
  let t9;
  if ($[3] !== scrollAmount) {
    t9 = direction => {
      const el_1 = scrollRef.current;
      if (!el_1) {
        return;
      }
      const amount = direction === "left" ? -scrollAmount : scrollAmount;
      el_1.scrollBy({
        left: amount,
        behavior: "smooth"
      });
    };
    $[3] = scrollAmount;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const scroll = t9;
  let t10;
  if ($[5] !== scroll) {
    t10 = e => {
      const el_2 = scrollRef.current;
      if (!el_2) {
        return;
      }
      bb43: switch (e.key) {
        case "ArrowRight":
          {
            e.preventDefault();
            scroll("right");
            break bb43;
          }
        case "ArrowLeft":
          {
            e.preventDefault();
            scroll("left");
            break bb43;
          }
        case "Home":
          {
            e.preventDefault();
            el_2.scrollTo({
              left: 0,
              behavior: "smooth"
            });
            break bb43;
          }
        case "End":
          {
            e.preventDefault();
            el_2.scrollTo({
              left: el_2.scrollWidth,
              behavior: "smooth"
            });
          }
      }
    };
    $[5] = scroll;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const handleKeyDown = t10;
  let t11;
  if ($[7] !== className) {
    t11 = cn("relative", className);
    $[7] = className;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== paddingClass) {
    t12 = cn("flex overflow-x-auto pb-4 outline-none [scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent] focus-visible:ring-2 focus-visible:ring-primary-500/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:h-1.5", paddingClass);
    $[9] = paddingClass;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  const t13 = `${gap}px`;
  let t14;
  if ($[11] !== t13) {
    t14 = {
      gap: t13
    };
    $[11] = t13;
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  let t15;
  if ($[13] !== exploreMore) {
    t15 = exploreMore && <Link href={exploreMore.href} className="flex w-[220px] shrink-0 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 transition-colors hover:border-gray-400 hover:bg-white"><div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"><ArrowRight className="h-5 w-5 text-gray-400" aria-hidden="true" /></div><p className="mt-3 text-sm font-black text-gray-600">{exploreMore.label}</p>{exploreMore.description && <p className="mt-1 text-xs text-gray-400">{exploreMore.description}</p>}</Link>;
    $[13] = exploreMore;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== children || $[16] !== handleKeyDown || $[17] !== t12 || $[18] !== t14 || $[19] !== t15) {
    t16 = <div ref={scrollRef} role="group" aria-label="Scrollable carousel. Use the left and right arrow keys to navigate." tabIndex={0} onKeyDown={handleKeyDown} className={t12} style={t14}>{children}{t15}</div>;
    $[15] = children;
    $[16] = handleKeyDown;
    $[17] = t12;
    $[18] = t14;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== arrowClassName || $[22] !== arrowIconClassName || $[23] !== canScrollLeft || $[24] !== scroll || $[25] !== showArrows || $[26] !== variant) {
    t17 = showArrows && canScrollLeft && <button onClick={() => scroll("left")} className={cn("absolute left-3 top-1/2 z-10 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-all", variant.button, arrowClassName)} aria-label="Scroll left"><ChevronLeft className={cn(variant.icon, arrowIconClassName)} /></button>;
    $[21] = arrowClassName;
    $[22] = arrowIconClassName;
    $[23] = canScrollLeft;
    $[24] = scroll;
    $[25] = showArrows;
    $[26] = variant;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== arrowClassName || $[29] !== arrowIconClassName || $[30] !== canScrollRight || $[31] !== scroll || $[32] !== showArrows || $[33] !== variant) {
    t18 = showArrows && canScrollRight && <button onClick={() => scroll("right")} className={cn("absolute right-3 top-1/2 z-10 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-all", variant.button, arrowClassName)} aria-label="Scroll right"><ChevronRight className={cn(variant.icon, arrowIconClassName)} /></button>;
    $[28] = arrowClassName;
    $[29] = arrowIconClassName;
    $[30] = canScrollRight;
    $[31] = scroll;
    $[32] = showArrows;
    $[33] = variant;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t11 || $[36] !== t16 || $[37] !== t17 || $[38] !== t18) {
    t19 = <div className={t11}>{t16}{t17}{t18}</div>;
    $[35] = t11;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  return t19;
}